import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
  TrendingUp, Users, CreditCard, Calendar, RefreshCw,
  ArrowUpRight, Building2, Wallet, UserCheck, ChevronDown
} from 'lucide-react';

interface LeadStats {
  bankId: string;
  bankName: string;
  remoteCustomer: number;
  creditCards: number;
  loans: number;
  total: number;
}

const LeadFlow = () => {
  const [leadStats, setLeadStats] = useState<LeadStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState('today');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [customEndDate, setCustomEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const getDateRangeFilter = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateRange) {
      case 'today':
        return { start: today, end: now };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: today };
      }
      case 'last_7_days': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo, end: now };
      }
      case 'last_30_days': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { start: monthAgo, end: now };
      }
      case 'this_month': {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: monthStart, end: now };
      }
      case 'last_month': {
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start: lastMonthStart, end: lastMonthEnd };
      }
      case 'custom':
        return {
          start: new Date(customStartDate),
          end: new Date(customEndDate + 'T23:59:59')
        };
      default:
        return { start: today, end: now };
    }
  };

  const fetchLeadStats = async () => {
    try {
      setIsLoading(true);
      const { start, end } = getDateRangeFilter();

      const [remoteCustomerData, cardData, loanData, bankOffersData] = await Promise.all([
        supabase
          .from('remote_customer_applications')
          .select('bank_name, created_at')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString()),
        supabase
          .from('card_applications')
          .select('bank_name, created_at')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString()),
        supabase
          .from('loan_applications')
          .select('selected_banks, created_at')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString()),
        supabase
          .from('bank_offers')
          .select('bank_id, bank_name')
          .eq('is_active', true)
          .order('display_order', { ascending: true })
      ]);

      if (remoteCustomerData.error || cardData.error || loanData.error || bankOffersData.error) {
        console.error('Error fetching lead stats:', {
          remoteCustomer: remoteCustomerData.error,
          card: cardData.error,
          loan: loanData.error,
          bankOffers: bankOffersData.error
        });
        return;
      }

      const banks = bankOffersData.data || [];
      const statsMap = new Map<string, LeadStats>();

      banks.forEach(bank => {
        statsMap.set(bank.bank_id, {
          bankId: bank.bank_id,
          bankName: bank.bank_name,
          remoteCustomer: 0,
          creditCards: 0,
          loans: 0,
          total: 0
        });
      });

      (remoteCustomerData.data || []).forEach(app => {
        const bankId = app.bank_name?.toLowerCase().replace(/\s+/g, '-') || '';
        if (statsMap.has(bankId)) {
          const stats = statsMap.get(bankId)!;
          stats.remoteCustomer++;
          stats.total++;
        }
      });

      (cardData.data || []).forEach(app => {
        const bankId = app.bank_name?.toLowerCase().replace(/\s+/g, '-') || '';
        if (statsMap.has(bankId)) {
          const stats = statsMap.get(bankId)!;
          stats.creditCards++;
          stats.total++;
        }
      });

      (loanData.data || []).forEach(app => {
        const selectedBanks = app.selected_banks || [];
        selectedBanks.forEach(bankName => {
          const bankId = bankName.toLowerCase().replace(/\s+/g, '-');
          if (statsMap.has(bankId)) {
            const stats = statsMap.get(bankId)!;
            stats.loans++;
            stats.total++;
          }
        });
      });

      const statsArray = Array.from(statsMap.values()).sort((a, b) => b.total - a.total);
      setLeadStats(statsArray);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching lead stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadStats();
    const interval = setInterval(fetchLeadStats, 30000);
    return () => clearInterval(interval);
  }, [dateRange, customStartDate, customEndDate]);

  const getFormattedDateRange = () => {
    if (dateRange === 'custom') {
      return `${new Date(customStartDate).toLocaleDateString('tr-TR')} - ${new Date(customEndDate).toLocaleDateString('tr-TR')}`;
    }

    switch (dateRange) {
      case 'today': return 'Bugün';
      case 'yesterday': return 'Dün';
      case 'last_7_days': return 'Son 7 Gün';
      case 'last_30_days': return 'Son 30 Gün';
      case 'this_month': return 'Bu Ay';
      case 'last_month': return 'Geçen Ay';
      default: return 'Bugün';
    }
  };

  const totalStats = leadStats.reduce((acc, bank) => ({
    remoteCustomer: acc.remoteCustomer + bank.remoteCustomer,
    creditCards: acc.creditCards + bank.creditCards,
    loans: acc.loans + bank.loans,
    total: acc.total + bank.total
  }), { remoteCustomer: 0, creditCards: 0, loans: 0, total: 0 });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Lead Akışı</h1>
              <p className="text-slate-600">Tüm bankaların gerçek zamanlı lead sayıları</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowCustomDateRange(!showCustomDateRange)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Calendar size={20} className="text-slate-500" />
                  <span>{getFormattedDateRange()}</span>
                  <ChevronDown size={16} className={`text-slate-500 transition-transform ${showCustomDateRange ? 'rotate-180' : ''}`} />
                </button>

                {showCustomDateRange && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-10">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Tarih Aralığı
                        </label>
                        <select
                          value={dateRange}
                          onChange={(e) => {
                            setDateRange(e.target.value);
                            if (e.target.value !== 'custom') {
                              setShowCustomDateRange(false);
                            }
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        >
                          <option value="today">Bugün</option>
                          <option value="yesterday">Dün</option>
                          <option value="last_7_days">Son 7 Gün</option>
                          <option value="last_30_days">Son 30 Gün</option>
                          <option value="this_month">Bu Ay</option>
                          <option value="last_month">Geçen Ay</option>
                          <option value="custom">Özel Tarih Aralığı</option>
                        </select>
                      </div>

                      {dateRange === 'custom' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Başlangıç
                            </label>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Bitiş
                            </label>
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => setShowCustomDateRange(false)}
                          className="px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                        >
                          Uygula
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={fetchLeadStats}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                <span>Yenile</span>
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${isLoading ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></div>
              <span>Son güncelleme: {lastUpdated.toLocaleTimeString('tr-TR')}</span>
            </div>
            <span>•</span>
            <span>Otomatik yenileme: Her 30 saniyede</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Toplam Lead</p>
                <p className="text-3xl font-bold text-slate-900">{totalStats.total}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight size={16} className="text-green-600" />
                  <span className="text-sm text-green-600">Aktif</span>
                </div>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <TrendingUp className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Müşteri Edinimi</p>
                <p className="text-3xl font-bold text-slate-900">{totalStats.remoteCustomer}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-sm text-slate-500">
                    %{totalStats.total > 0 ? ((totalStats.remoteCustomer / totalStats.total) * 100).toFixed(1) : '0'}
                  </span>
                </div>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <UserCheck className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Kredi Kartı</p>
                <p className="text-3xl font-bold text-slate-900">{totalStats.creditCards}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-sm text-slate-500">
                    %{totalStats.total > 0 ? ((totalStats.creditCards / totalStats.total) * 100).toFixed(1) : '0'}
                  </span>
                </div>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <CreditCard className="text-purple-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Kredi</p>
                <p className="text-3xl font-bold text-slate-900">{totalStats.loans}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-sm text-slate-500">
                    %{totalStats.total > 0 ? ((totalStats.loans / totalStats.total) * 100).toFixed(1) : '0'}
                  </span>
                </div>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Wallet className="text-orange-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {isLoading && leadStats.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7BA45] mx-auto mb-4"></div>
              <p className="text-slate-600">Yükleniyor...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {leadStats.map((bank) => (
              <div
                key={bank.bankId}
                className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-slate-100 p-2 rounded-lg">
                        <Building2 className="text-slate-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{bank.bankName}</h3>
                        <p className="text-sm text-slate-500">Toplam: {bank.total} lead</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2">
                        <UserCheck size={18} className="text-green-600" />
                        <span className="text-sm font-medium text-slate-700">Müşteri Edinimi</span>
                      </div>
                      <span className="text-lg font-bold text-green-600">{bank.remoteCustomer}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="flex items-center gap-2">
                        <CreditCard size={18} className="text-purple-600" />
                        <span className="text-sm font-medium text-slate-700">Kredi Kartı</span>
                      </div>
                      <span className="text-lg font-bold text-purple-600">{bank.creditCards}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex items-center gap-2">
                        <Wallet size={18} className="text-orange-600" />
                        <span className="text-sm font-medium text-slate-700">Kredi</span>
                      </div>
                      <span className="text-lg font-bold text-orange-600">{bank.loans}</span>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 rounded-b-xl">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Toplam Dönüşüm</span>
                    <span className="font-semibold text-slate-900">{bank.total}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && leadStats.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Seçilen tarih aralığında lead bulunamadı</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadFlow;
